import { BigNumber } from '@ethersproject/bignumber'
import { useEffect, useState } from 'react'
import { DEPOSIT_GAS_LIMIT, EXECUTE_DEPOSIT_GAS_LIMIT } from '../../constants'
import {
  useApprovalForDelay,
  useBalance,
  useCurrencySelect,
  useEthers,
  useLiquidities,
  useNumberInput,
  usePair,
} from '../../hooks'
import { CurrencyValue, formatCurrency, Pair } from '../../model'
import { TokenPrice } from '../../providers/apiTokenPrices/model'
import { Settings, useApiTokenPrices, useGasPrice, useSettings } from '../../providers'
import { BN_10_18, getAffordableMaxValueFor, getCurrencyValues, denormalizeFrom18Decimals } from '../../utils'

export enum LiquidityState {
  WALLET_NOT_CONNECTED = 'WALLET_NOT_CONNECTED',
  TOKEN_NOT_SELECTED = 'TOKEN_NOT_SELECTED',
  LOADING = 'LOADING',
  PAIR_DOES_NOT_EXIST = 'PAIR_DOES_NOT_EXIST',
  INSUFFICIENT_FIRST_BALANCE = 'INSUFFICIENT_FIRST_BALANCE',
  INSUFFICIENT_SECOND_BALANCE = 'INSUFFICIENT_SECOND_BALANCE',
  INSUFFICIENT_BOTH_BALANCES = 'INSUFFICIENT_BOTH_BALANCES',
  NEED_FIRST_APPROVAL = 'NEED_FIRST_APPROVAL',
  NEED_SECOND_APPROVAL = 'NEED_SECOND_APPROVAL',
  NEED_BOTH_APPROVALS = 'NEED_BOTH_APPROVALS',
  EMPTY = 'EMPTY',
  NO_ISSUE = 'NO_ISSUE',
  MISSING_INPUT = 'MISSING_INPUT',
  INSUFFICIENT_FIRST_RESERVE = 'INSUFFICIENT_FIRST_RESERVE',
  INSUFFICIENT_SECOND_RESERVE = 'INSUFFICIENT_SECOND_RESERVE',
  TOKEN0_ABSOLUTE_LIMIT = 'TOKEN0_ABSOLUTE_LIMIT',
  TOKEN0_RELATIVE_LIMIT = 'TOKEN0_RELATIVE_LIMIT',
  TOKEN1_ABSOLUTE_LIMIT = 'TOKEN1_ABSOLUTE_LIMIT',
  TOKEN1_RELATIVE_LIMIT = 'TOKEN1_RELATIVE_LIMIT',
}

const getAffordableMaxValue = getAffordableMaxValueFor(EXECUTE_DEPOSIT_GAS_LIMIT, DEPOSIT_GAS_LIMIT)
const INSUFFICIENT_ETH_MSG = 'Insufficient ETH balance for gas prepay and input. Please add more ETH to your wallet.'

export function useSuggestedDeposit(settings: Settings, pair?: Pair) {
  if (!pair) {
    return true
  }
  return settings.useSuggestedDeposit
}

export function useAddLiquidity() {
  const { account } = useEthers()
  const currencies = useCurrencySelect()
  const [controlled, setControlled] = useState<'first' | 'second'>('first')
  const [firstValue, setFirstValue] = useNumberInput(currencies.first?.decimals)
  const [secondValue, setSecondValue] = useNumberInput(currencies.second?.decimals)
  const { pair, exists } = usePair(currencies.first, currencies.second)
  const firstBalance = useBalance(currencies.first)
  const secondBalance = useBalance(currencies.second)
  const firstApproval = useApprovalForDelay(currencies.first)
  const secondApproval = useApprovalForDelay(currencies.second)
  const { settings } = useSettings()
  const apiTokenPrices = useApiTokenPrices()
  const liquidities = useLiquidities()
  const gasPrice = useGasPrice()

  function onFirstChange(value: string) {
    if (currencies.first.isNative() && gasPrice) {
      gasPrice.setMaxGasFeeForEthMax(undefined)
    }
    _onFirstChange(value)
  }

  function _onFirstChange(value: string) {
    setFirstValue(value)
    setControlled('first')
  }

  function onSecondChange(value: string) {
    if (currencies.second.isNative() && gasPrice) {
      gasPrice.setMaxGasFeeForEthMax(undefined)
    }
    _onSecondChange(value)
  }

  function _onSecondChange(value: string) {
    setSecondValue(value)
    setControlled('second')
  }

  function setFirstMax() {
    if (firstBalance) {
      if (firstBalance.currency.isNative()) {
        if (gasPrice && gasPrice.delayGasPrice && gasPrice.networkGasPrice && gasPrice.maxGasFee) {
          const { delayGasPrice, maxGasFee, setMaxGasFeeForEthMax } = gasPrice
          const affordableMaxValue = getAffordableMaxValue(firstBalance, delayGasPrice, maxGasFee, settings)
          setMaxGasFeeForEthMax(maxGasFee)
          if (affordableMaxValue.lte(0)) {
            alert(INSUFFICIENT_ETH_MSG)
          } else {
            _onFirstChange(new CurrencyValue(firstBalance.currency, affordableMaxValue).toString())
          }
        }
      } else {
        _onFirstChange(firstBalance.toString())
      }
    }
  }

  function setSecondMax() {
    if (secondBalance) {
      if (secondBalance.currency.isNative()) {
        if (gasPrice && gasPrice.delayGasPrice && gasPrice.networkGasPrice && gasPrice.maxGasFee) {
          const { delayGasPrice, maxGasFee, setMaxGasFeeForEthMax } = gasPrice
          const affordableMaxValue = getAffordableMaxValue(secondBalance, delayGasPrice, maxGasFee, settings)
          setMaxGasFeeForEthMax(maxGasFee)
          if (affordableMaxValue.lte(0)) {
            alert(INSUFFICIENT_ETH_MSG)
          } else {
            _onSecondChange(new CurrencyValue(secondBalance.currency, affordableMaxValue).toString())
          }
        }
      } else {
        _onSecondChange(secondBalance.toString())
      }
    }
  }

  const [firstCurrencyValue, firstCurrencyValue18] = getCurrencyValues(currencies.first, firstValue)
  const [secondCurrencyValue, secondCurrencyValue18] = getCurrencyValues(currencies.second, secondValue)

  const firstPriceSymbol = currencies.first.isNative() ? 'WETH' : currencies.first.symbol
  const secondPriceSymbol = currencies.second.isNative() ? 'WETH' : currencies.second.symbol
  const firstTokenPrice = apiTokenPrices?.[firstPriceSymbol]
  const secondTokenPrice = apiTokenPrices?.[secondPriceSymbol]

  const [firstUsdValue18, firstUsdValue] = getUsdTokenValues(firstCurrencyValue18, firstTokenPrice)
  const [secondUsdValue18, secondUsdValue] = getUsdTokenValues(secondCurrencyValue18, secondTokenPrice)

  const firstReserve = pair?.getFirstReserve()
  const secondReserve = pair?.getSecondReserve()
  const fraction = pair?.getReservesFraction(firstTokenPrice, secondTokenPrice)

  useEffect(() => {
    if (controlled === 'first') {
      if (currencies.first && currencies.second) {
        if (pair?.isEmpty() === false) {
          if (useSuggestedDeposit(settings, pair)) {
            if (firstUsdValue18 && secondTokenPrice && fraction) {
              const secondValue18 =
                !secondTokenPrice.price.isZero() && firstUsdValue18.mul(fraction).div(secondTokenPrice.price)
              if (secondValue18) {
                setSecondValue(
                  new CurrencyValue(
                    secondCurrencyValue.currency,
                    denormalizeFrom18Decimals(secondValue18, secondCurrencyValue.currency.decimals)
                  ).toString()
                )
              } else {
                setSecondValue('')
              }
            }
          } else if (firstValue !== '' && secondValue === '') {
            setSecondValue('0')
          }
        }
      } else {
        setSecondValue('')
      }
    }
  }, [
    controlled,
    firstUsdValue18,
    secondTokenPrice,
    fraction,
    currencies.first,
    currencies.second,
    settings.useSuggestedDeposit,
  ])

  useEffect(() => {
    if (controlled === 'second') {
      if (currencies.first && currencies.second) {
        if (pair?.isEmpty() === false) {
          if (useSuggestedDeposit(settings, pair)) {
            if (secondUsdValue18 && firstTokenPrice && fraction) {
              const firstValue18 =
                !fraction.isZero() &&
                !firstTokenPrice.price.isZero() &&
                secondUsdValue18.mul(BN_10_18).mul(BN_10_18).div(fraction).div(firstTokenPrice.price)
              if (firstValue18) {
                setFirstValue(
                  new CurrencyValue(
                    firstCurrencyValue.currency,
                    denormalizeFrom18Decimals(firstValue18, firstCurrencyValue.currency.decimals)
                  ).toString()
                )
              } else {
                setFirstValue('')
              }
            }
          } else if (secondValue !== '' && firstValue === '') {
            setFirstValue('0')
          }
        }
      } else {
        setFirstValue('')
      }
    }
  }, [
    controlled,
    secondUsdValue18,
    firstTokenPrice,
    fraction,
    currencies.first,
    currencies.second,
    settings.useSuggestedDeposit,
  ])

  const liquidityBalance = (pair && liquidities && liquidities[pair.address]) ?? BigNumber.from(0)
  const { liquidityValue, totalLiquiditySupply, swapDepositFirstAmount, swapDepositSecondAmount } =
    getLiquidityQuantities(firstCurrencyValue, secondCurrencyValue, pair)

  const absoluteLimit = swapDepositFirstAmount?.value.gt(0)
    ? pair?.getFirstAbsoluteLimit()
    : pair?.getSecondAbsoluteLimit()
  const relativeLimit = swapDepositFirstAmount?.value.gt(0)
    ? pair?.getFirstRelativeLimit()
    : pair?.getSecondRelativeLimit()

  let state = LiquidityState.NO_ISSUE
  if (!currencies.first || !currencies.second) {
    state = LiquidityState.TOKEN_NOT_SELECTED
  } else if (!exists) {
    state = LiquidityState.PAIR_DOES_NOT_EXIST
  } else if (
    !pair ||
    !firstBalance ||
    !secondBalance ||
    !firstCurrencyValue ||
    !secondCurrencyValue ||
    !firstApproval ||
    !secondApproval ||
    !firstReserve ||
    !secondReserve ||
    !absoluteLimit ||
    !relativeLimit
  ) {
    state = LiquidityState.LOADING
  } else if (!pair.isEmpty() && swapDepositFirstAmount && swapDepositFirstAmount.value.gte(firstReserve.value)) {
    state = LiquidityState.INSUFFICIENT_FIRST_RESERVE
  } else if (!pair.isEmpty() && swapDepositSecondAmount && swapDepositSecondAmount.value.gte(secondReserve.value)) {
    state = LiquidityState.INSUFFICIENT_SECOND_RESERVE
  } else if (!pair.isEmpty() && swapDepositFirstAmount && swapDepositFirstAmount.value.gte(absoluteLimit.value)) {
    state = LiquidityState.TOKEN0_ABSOLUTE_LIMIT
  } else if (
    !pair.isEmpty() &&
    swapDepositFirstAmount &&
    swapDepositFirstAmount.value.mul(BN_10_18).div(firstReserve.value).gte(relativeLimit.value)
  ) {
    state = LiquidityState.TOKEN0_RELATIVE_LIMIT
  } else if (!pair.isEmpty() && swapDepositSecondAmount && swapDepositSecondAmount.value.gte(absoluteLimit.value)) {
    state = LiquidityState.TOKEN1_ABSOLUTE_LIMIT
  } else if (
    !pair.isEmpty() &&
    swapDepositSecondAmount &&
    swapDepositSecondAmount.value.mul(BN_10_18).div(secondReserve.value).gte(relativeLimit.value)
  ) {
    state = LiquidityState.TOKEN1_RELATIVE_LIMIT
  } else if (firstBalance.lt(firstCurrencyValue) && secondBalance.lt(secondCurrencyValue)) {
    state = LiquidityState.INSUFFICIENT_BOTH_BALANCES
  } else if (firstBalance.lt(firstCurrencyValue)) {
    state = LiquidityState.INSUFFICIENT_FIRST_BALANCE
  } else if (secondBalance.lt(secondCurrencyValue)) {
    state = LiquidityState.INSUFFICIENT_SECOND_BALANCE
  } else if (!firstValue || !secondValue) {
    state = LiquidityState.MISSING_INPUT
  } else if (firstApproval.lt(firstCurrencyValue) && secondApproval.lt(secondCurrencyValue)) {
    state = LiquidityState.NEED_BOTH_APPROVALS
  } else if (firstApproval.lt(firstCurrencyValue)) {
    state = LiquidityState.NEED_FIRST_APPROVAL
  } else if (secondApproval.lt(secondCurrencyValue)) {
    state = LiquidityState.NEED_SECOND_APPROVAL
  } else if (pair.isEmpty()) {
    state = LiquidityState.EMPTY
  }

  if (!account) {
    state = LiquidityState.WALLET_NOT_CONNECTED
  }

  return {
    currencies,
    firstUsdValue,
    firstUsdValue18,
    firstValue,
    setFirstValue: onFirstChange,
    setFirstMax,
    secondUsdValue,
    secondUsdValue18,
    secondValue,
    setSecondValue: onSecondChange,
    setSecondMax,
    pair,
    state,
    liquidityValue,
    liquidityBalance,
    totalLiquiditySupply,
    suggestedDeposit: settings.useSuggestedDeposit,
  }
}

function getLiquidityQuantities(firstCurrencyValue: CurrencyValue, secondCurrencyValue: CurrencyValue, pair?: Pair) {
  const liquidity =
    firstCurrencyValue &&
    secondCurrencyValue &&
    pair &&
    pair.getLiquidity(firstCurrencyValue, secondCurrencyValue, false)
  const liquidityValue = liquidity?.liquidity
  const swapDepositFirstAmount = liquidity?.swapDepositFirstAmount
  const swapDepositSecondAmount = liquidity?.swapDepositSecondAmount

  const totalLiquiditySupply = (pair && pair.getTotalSupply()) ?? BigNumber.from(0)

  return { liquidityValue, totalLiquiditySupply, swapDepositFirstAmount, swapDepositSecondAmount }
}

function getUsdTokenValues(
  currencyValue18: BigNumber,
  tokenPrice?: TokenPrice
): [BigNumber, string] | [undefined, undefined] {
  if (tokenPrice) {
    const usdValue18 = currencyValue18.mul(tokenPrice.price).div(BN_10_18)
    return [usdValue18, `~$${formatCurrency(18, usdValue18, 2)}`]
  }
  return [undefined, undefined]
}
